import type { KeyboardShortcutId } from "@/types/keyboardShortcuts.ts";
import { resolveShortcutKeys, type ShortcutBindings } from "./bindings.ts";
import { hasRealModifier, normalizeCombo, sequenceSteps } from "./keyNames.ts";
import { isMac } from "./platform.ts";
import {
  ALL_SHORTCUTS,
  SHORTCUT_GROUP_OF,
  type ShortcutDef,
  type ShortcutGroupId,
} from "./shortcuts.ts";

/**
 * Groups whose shortcuts are live on every screen. A key shared between two
 * of these, or between one of these and anything, fires twice. The calendar
 * and threads groups are different surfaces, so `j` meaning "next period" on
 * one and "next thread" on the other is a deliberate reuse, not a conflict.
 */
const GLOBAL_GROUPS: ReadonlySet<ShortcutGroupId> = new Set([
  "agent",
  "navigation",
  "composer",
  "system",
]);

const groupsCoexist = (a: ShortcutGroupId, b: ShortcutGroupId): boolean =>
  a === b || GLOBAL_GROUPS.has(a) || GLOBAL_GROUPS.has(b);

/**
 * Whether two combos would answer the same keystroke on this platform. Equal
 * combos do, compared after `mod` is resolved, since `mod+n` and `ctrl+n` are
 * the same key off macOS. So do a bare key and a sequence that starts with it:
 * the sequence layer does not consume its first key, so `t` would fire "today"
 * and arm `t c` together.
 */
const combosCollide = (a: string, b: string, mac: boolean): boolean => {
  const normA = normalizeCombo(a, mac);
  const normB = normalizeCombo(b, mac);
  if (normA === normB) return true;
  const stepsA = sequenceSteps(normA);
  const stepsB = sequenceSteps(normB);
  if (stepsA.length === 1 && stepsB.length > 1 && !hasRealModifier(normA)) {
    return stepsB[0] === normA;
  }
  if (stepsB.length === 1 && stepsA.length > 1 && !hasRealModifier(normB)) {
    return stepsA[0] === normB;
  }
  return false;
};

/**
 * The other shortcuts that `combo` would also trigger if bound to `id`, given
 * everyone's effective keys. Only shortcuts that can be live at the same time
 * count (see `GLOBAL_GROUPS`). `mac` picks which modifier `mod` means; it
 * defaults to this machine's.
 */
export const findShortcutConflicts = (
  id: KeyboardShortcutId,
  combo: string,
  bindings: ShortcutBindings,
  mac: boolean = isMac(),
): ShortcutDef[] => {
  const group = SHORTCUT_GROUP_OF[id];
  return ALL_SHORTCUTS.filter(
    (other) =>
      other.id !== id &&
      groupsCoexist(group, SHORTCUT_GROUP_OF[other.id]) &&
      resolveShortcutKeys(other, bindings).some((key) => combosCollide(combo, key, mac)),
  );
};
